/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import getQuote from 'src/api/quote/get-quote';
import getQuotes, { GetQuotesResponse } from 'src/api/quote/get-quotes';
import { HandleSortChangeParams } from 'src/components/common/Table/types';
import { Quote, QuotesFilters, QuoteSliceState } from '../types/quoteSlice';
import { RootState } from '../store';

const initialState: QuoteSliceState = {
	quote: { isInitialized: false, data: null },
	quotes: {
		isInitialized: false,
		data: {
			data: { quotes: [], users: [] },
			orderBy: 'createdAt',
			sortOrder: 'desc',
			filters: {},
		},
	},
};

export const selectQuote = (state: RootState) => state.quote.quote;

export const getQuoteThunk = createAsyncThunk<Quote, { id: string; withLoading?: boolean }>(
	'quote/get',
	({ id }, { rejectWithValue }) => {
		try {
			return getQuote({ id });
		} catch (e) {
			return rejectWithValue(e);
		}
	},
);

export const selectQuotes = (state: RootState) => state.quote.quotes;

export const getQuotesThunk = createAsyncThunk<GetQuotesResponse, { withLoading?: boolean }>(
	'quotes/get',
	async (_, { getState, rejectWithValue, signal }) => {
		try {
			const state = getState() as RootState;
			const quotes = selectQuotes(state);

			return await getQuotes({
				payload: { orderBy: quotes.data.orderBy, sortOrder: quotes.data.sortOrder, ...quotes.data.filters },
				signal,
			});
		} catch (e) {
			return rejectWithValue(e);
		}
	},
);

const quoteSlice = createSlice({
	name: 'data',
	initialState,
	reducers: {
		sortQuotes: (state, { payload: { order, orderBy } }: PayloadAction<HandleSortChangeParams>) => {
			state.quotes.data.orderBy = orderBy;
			state.quotes.data.sortOrder = order;
		},
		setQuotesFilters: (state, { payload }: PayloadAction<QuotesFilters>) => {
			state.quotes.data.filters = { ...state.quotes.data.filters, ...payload };
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getQuoteThunk.pending, (state, { meta }) => {
			if (meta.arg.withLoading) state.quote.isInitialized = false;
		});
		builder.addCase(getQuoteThunk.fulfilled, (state, { payload }) => {
			state.quote.data = payload;
			state.quote.isInitialized = true;
		});
		builder.addCase(getQuoteThunk.rejected, (state, { error }) => {
			state.quote.isInitialized = true;
			state.quote.error = error;
		});

		builder.addCase(getQuotesThunk.pending, (state, { meta }) => {
			if (meta.arg.withLoading) state.quotes.isInitialized = false;
		});
		builder.addCase(getQuotesThunk.fulfilled, (state, { payload }) => {
			state.quotes.data.data = payload;
			state.quotes.isInitialized = true;
		});
		builder.addCase(getQuotesThunk.rejected, (state, { error }) => {
			state.quotes.isInitialized = true;
			state.quotes.error = error;
		});
	},
});

export const { sortQuotes, setQuotesFilters } = quoteSlice.actions;

export default quoteSlice.reducer;
